import { create } from 'zustand';
import { authService } from '../services/api';

const CLE_JETON = 'jeta_token';

// Etat d'un visiteur non authentifie. Sert aussi bien a la deconnexion qu'a la
// remise a zero avant une nouvelle connexion : aucun reliquat du compte
// precedent ne doit subsister.
const ETAT_DECONNECTE = { user: null, token: null, loading: false, error: null };

// Le service worker garde en cache les reponses GET de /demandes et
// /notifications. Elles appartiennent au compte qui vient de partir : sans
// purge, le compte suivant peut se les voir servir des que le reseau flanche,
// la strategie etant « reseau d'abord, cache en secours ».
const purgerCachesApi = async () => {
  if (typeof caches === 'undefined') return;
  try {
    const cles = await caches.keys();
    await Promise.all(cles.filter((c) => c.indexOf('-api') !== -1).map((c) => caches.delete(c)));
  } catch { /* le cache est un confort, son echec ne doit pas bloquer la sortie */ }
};

const useAuthStore = create((set, get) => ({
  user: null,
  token: localStorage.getItem(CLE_JETON) || null,
  loading: false,
  error: null,

  login: async (email, motDePasse) => {
    // Table rase avant toute chose : si un compte precedent est encore en
    // memoire, son nom ne doit pas s'afficher pendant l'authentification.
    set({ ...ETAT_DECONNECTE, loading: true });
    try {
      const res = await authService.login({ email, motDePasse });
      localStorage.setItem(CLE_JETON, res.token);
      const userData = res.user || res.data;
      set({ user: userData, token: res.token, loading: false });
      return userData;
    } catch (err) {
      set({ error: err.message || 'Erreur de connexion', loading: false });
      throw err;
    }
  },

  logout: async () => {
    // On vide AVANT l'appel reseau. L'ordre inverse laissait le nom et le role
    // du compte precedent a l'ecran pendant tout l'aller-retour — et
    // indefiniment si le reseau ne repondait pas.
    localStorage.removeItem(CLE_JETON);
    set({ ...ETAT_DECONNECTE });
    // Le motif de deconnexion (sessionStorage) est volontairement conserve :
    // la page de connexion l'affiche apres une expiration de session.
    purgerCachesApi();
    try { await authService.logout(); } catch {}
  },

  fetchMe: async () => {
    // Jeton au moment du depart : la reponse ne sera appliquee que si la
    // session n'a pas change entre-temps. Sans ce controle, un /auth/me lance
    // par le compte A et resolu apres la connexion du compte B ecrasait B par
    // A — le bandeau affichait alors l'utilisateur precedent.
    const jetonAppel = get().token;
    try {
      const res = await authService.me();
      if (get().token !== jetonAppel) return;
      set({ user: res.user || res.data });
    } catch {
      if (get().token !== jetonAppel) return;
      localStorage.removeItem(CLE_JETON);
      set({ ...ETAT_DECONNECTE });
    }
  },

  // Vérifier si l'utilisateur a un rôle donné
  hasRole: (role, filialeId) => {
    const { user } = get();
    if (!user) return false;
    if (user.isGlobal) return true;
    return user.roles.some(r =>
      r.role === role && (!filialeId || r.filiale._id === filialeId)
    );
  },

  isPDG: () => get().hasRole('pdg'),
  isAdmin: () => get().hasRole('admin')
}));

export default useAuthStore;
